import { useState, useMemo } from "react";
import { fixedSlots } from "@/lib/listIdentity";
import { useQuery } from "@tanstack/react-query";
import { motion, AnimatePresence } from "framer-motion";
import { api } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { SLOT_META, RTEXT, RRING, GearHoverCard, StatChips, TIER_COLOR } from "@/components/GearSlots";
import { Swords, Shield, Gem, Sparkles, FlaskConical, X, Search, Plus, Package, ChevronLeft, ChevronRight, Lock } from "lucide-react";

const GEAR_ORDER = ["weapon", "armor", "trinket", "relic"];
const ITEM_SLOTS = 3;
const PAGE = 8;
const STAT_KEYS = [["atk", "ATK"], ["def", "DEF"], ["spd", "SPD"], ["hp", "HP"]];

// Predicted flat stat deltas an item would add, from its eff %-mods applied to the
// card's current stats — used for the live "telemetry" preview on hover.
function deltasFor(item, stats) {
  if (!item) return {};
  const e = item.eff || {};
  const out = {};
  STAT_KEYS.forEach(([k]) => {
    const pct = e[`${k}_pct`];
    if (pct) out[k] = Math.round((stats[k] || 0) * pct);
  });
  return out;
}

function StatTelemetry({ stats, preview }) {
  return (
    <div className="grid grid-cols-4 gap-2" data-testid="loadout-telemetry">
      {STAT_KEYS.map(([k, label]) => {
        const d = preview?.[k] || 0;
        return (
          <div key={k} className="rounded-lg bg-black/40 border border-white/8 px-2 py-1.5 text-center">
            <div className="font-mono text-[8px] uppercase tracking-[0.2em] text-white/35">{label}</div>
            <div className="font-display text-base text-white leading-tight">{stats[k] ?? "—"}</div>
            <AnimatePresence>
              {d !== 0 && (
                <motion.div initial={{ opacity: 0, y: -3 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }}
                  className={`font-mono text-[9px] leading-none ${d > 0 ? "text-emerald-400" : "text-rose-400"}`}>
                  {d > 0 ? "+" : ""}{d}
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        );
      })}
    </div>
  );
}

function GearCell({ slot, item, active, canEdit, onSelect, onUnequip }) {
  const M = SLOT_META[slot];
  const Icon = M.icon;
  return (
    <motion.div
      whileHover={canEdit ? { y: -3 } : undefined}
      onClick={canEdit ? () => onSelect({ type: "gear", slot }) : undefined}
      role={canEdit ? "button" : undefined}
      data-testid={`slot-${slot}`}
      className={`group relative min-h-[76px] rounded-xl border flex items-start gap-3 px-3 py-2.5 transition-colors
        ${active ? "border-cyan-400/70 bg-cyan-400/[0.07] shadow-[0_0_20px_rgba(34,211,238,0.18)]"
          : item ? `${RRING[item.rarity] || "border-white/15"} bg-white/[0.04]`
          : "border-dashed border-white/12 bg-white/[0.015]"}
        ${canEdit ? "cursor-pointer hover:border-cyan-400/50" : ""}`}
    >
      {item && <GearHoverCard it={item} slotLabel={M.label} />}
      <div className={`h-9 w-9 rounded-lg flex items-center justify-center shrink-0 border ${item ? `${RRING[item.rarity]} bg-black/30` : "border-white/10 bg-white/[0.03]"}`}>
        <Icon className={`h-4 w-4 ${item ? RTEXT[item.rarity] || "text-white/70" : "text-white/25"}`} />
      </div>
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-1.5">
          <span className="font-mono text-[9px] uppercase tracking-[0.22em] text-white/40">{M.label}</span>
          {item && (
            <span className="font-mono text-[9px] font-bold rounded px-1 border leading-none py-0.5"
              style={{ color: TIER_COLOR[item.tier], borderColor: `${TIER_COLOR[item.tier]}55`, background: `${TIER_COLOR[item.tier]}12` }}>
              T{item.tier}
            </span>
          )}
        </div>
        {item ? (
          <>
            <div className={`text-xs font-medium leading-snug line-clamp-2 ${RTEXT[item.rarity] || "text-white"}`}>
              {item.name}
              {item.critical_forge && (
                <span className="ml-1 inline-flex items-center gap-0.5 rounded-full border border-amber-300/60 bg-amber-400/20 px-1.5 py-0.5 font-mono text-[8px] uppercase tracking-[0.18em] text-amber-200 align-middle" title={`Critical Forge — every stat multiplied ×${item.critical_forge.multiplier}`} data-testid={`slot-${slot}-crit-badge`}>
                  ★ CRIT ×{item.critical_forge.multiplier}
                </span>
              )}
            </div>
            <div data-testid={`slot-${slot}-benefit`}><StatChips it={item} max={4} /></div>
          </>
        ) : (
          <div className="text-[11px] text-white/30 flex items-center gap-1 mt-0.5">{canEdit ? <><Plus className="h-3 w-3" /> Equip</> : "Empty"}</div>
        )}
      </div>
      {item && canEdit && (
        <button data-testid={`unequip-${item.id}`} onClick={(e) => { e.stopPropagation(); onUnequip(item.id); }} title="Unequip"
          className="absolute top-1.5 right-1.5 h-5 w-5 rounded-full bg-rose-500/90 hover:bg-rose-400 text-white flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
          <X className="h-3 w-3" />
        </button>
      )}
    </motion.div>
  );
}

export function LoadoutManager({ card, canEdit, onEquip, onUnequip, equipping }) {
  const [pick, setPick] = useState(null);    // {type:'gear'|'item', slot}
  const [filter, setFilter] = useState("");
  const [page, setPage] = useState(0);
  const [hover, setHover] = useState(null);

  const { data: state, isLoading } = useQuery({
    queryKey: ["adv-state-equip"],
    queryFn: async () => (await api.get("/adventure/state")).data,
    enabled: !!canEdit,
  });

  const gearBySlot = useMemo(() => {
    const m = {};
    (card.gear || []).forEach((g) => { if (g.slot) m[g.slot] = g; });
    return m;
  }, [card.gear]);
  const loadout = (card.loadout || []).slice(0, ITEM_SLOTS);
  const stats = card.arena || {};

  const select = (sel) => { setPick(sel); setFilter(""); setPage(0); setHover(null); };

  const isGear = pick?.type === "gear";
  const pool = useMemo(() => {
    if (!pick) return [];
    const all = isGear ? (state?.gear || []) : (state?.consumables || []);
    return all
      .filter((it) => (isGear && pick.slot ? it.slot === pick.slot : true))
      .filter((it) => !filter || (it.name || "").toLowerCase().includes(filter.toLowerCase()));
  }, [pick, isGear, state, filter]);
  const pages = Math.max(1, Math.ceil(pool.length / PAGE));
  const cur = Math.min(page, pages - 1);
  const shown = pool.slice(cur * PAGE, cur * PAGE + PAGE);
  const preview = deltasFor(hover, stats);

  return (
    <div className="grid grid-cols-1 lg:grid-cols-12 gap-4" data-testid="loadout-manager">
      {/* LEFT — the loadout */}
      <div className={canEdit ? "lg:col-span-7 space-y-3" : "lg:col-span-12 space-y-3"}>
        <StatTelemetry stats={stats} preview={preview} />

        <div>
          <div className="font-mono text-[9px] uppercase tracking-[0.3em] text-cyan-300/70 mb-1.5">Gear</div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
            {GEAR_ORDER.map((slot) => (
              <GearCell key={slot} slot={slot} item={gearBySlot[slot]} canEdit={canEdit}
                active={pick?.type === "gear" && pick.slot === slot}
                onSelect={select} onUnequip={onUnequip} />
            ))}
          </div>
        </div>

        <div>
          <div className="font-mono text-[9px] uppercase tracking-[0.3em] text-fuchsia-300/70 mb-1.5">Battle Items · {loadout.length}/{ITEM_SLOTS}</div>
          <div className="grid grid-cols-3 gap-2">
            {fixedSlots(ITEM_SLOTS).map(({ id, index: i }) => {
              const it = loadout[i];
              const active = pick?.type === "item";
              return (
                <motion.div key={id} whileHover={canEdit && !it ? { y: -2 } : undefined}
                  onClick={canEdit && !it ? () => select({ type: "item" }) : undefined}
                  role={canEdit && !it ? "button" : undefined}
                  data-testid={`item-slot-${i}`}
                  className={`group relative h-14 rounded-lg border flex items-center gap-1.5 px-2 transition-colors
                    ${active && !it ? "border-fuchsia-400/70 bg-fuchsia-400/[0.07]" : it ? `${RRING[it.rarity] || "border-white/15"} bg-white/[0.04]` : "border-dashed border-white/12 bg-white/[0.015]"}
                    ${canEdit && !it ? "cursor-pointer hover:border-fuchsia-400/50" : ""}`}>
                  {it && <GearHoverCard it={it} slotLabel="Battle Item" />}
                  <FlaskConical className={`h-3.5 w-3.5 shrink-0 ${it ? RTEXT[it.rarity] || "text-white/70" : "text-white/25"}`} />
                  <span className={`font-mono text-[10px] truncate ${it ? RTEXT[it.rarity] || "text-white/80" : "text-white/30"}`}>{it ? it.name : (canEdit ? "+ add" : "empty")}</span>
                  {it && canEdit && (
                    <button data-testid={`unequip-${it.id}`} onClick={(e) => { e.stopPropagation(); onUnequip(it.id); }} title="Remove"
                      className="absolute -top-1.5 -right-1.5 h-4 w-4 rounded-full bg-rose-500/90 text-white flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                      <X className="h-2.5 w-2.5" />
                    </button>
                  )}
                </motion.div>
              );
            })}
          </div>
        </div>
        {canEdit && <div className="text-white/30 text-[10px]" data-testid="gear-edit-hint">Tap a slot to equip from your bag. Gear stays on this card everywhere — and travels with it in trades.</div>}
      </div>

      {/* RIGHT — docked Arsenal picker */}
      {canEdit && (
        <div className="lg:col-span-5">
          <div className="rounded-xl border border-white/8 bg-[#0B0F19]/80 backdrop-blur-xl overflow-hidden flex flex-col h-[330px] lg:h-full lg:min-h-[330px]" data-testid="equip-picker">
            <div className="px-3 py-2 border-b border-white/8 flex items-center gap-2">
              <Package className="h-3.5 w-3.5 text-cyan-300" />
              <span className="font-mono text-[10px] uppercase tracking-[0.22em] text-white/60 capitalize">
                {pick ? (isGear ? `${pick.slot} arsenal` : "battle items") : "Your arsenal"}
              </span>
            </div>
            {!pick ? (
              <div className="flex-1 flex flex-col items-center justify-center text-center px-5 gap-2 text-white/35">
                <Swords className="h-7 w-7 text-white/15" />
                <p className="text-[11px]">Select a gear or item slot on the left to browse your bag and equip something.</p>
              </div>
            ) : (
              <>
                <div className="p-2 border-b border-white/8">
                  <div className="relative">
                    <Search className="absolute left-2 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-white/30" />
                    <Input data-testid="equip-filter" placeholder="Filter by name…" value={filter}
                      onChange={(e) => { setFilter(e.target.value); setPage(0); }}
                      className="h-8 pl-7 bg-black/30 border-white/10 text-xs" />
                  </div>
                </div>
                <div className="flex-1 overflow-y-auto p-2 space-y-1.5">
                  {isLoading ? (
                    <div className="text-white/40 text-xs py-6 text-center">Loading your bag…</div>
                  ) : pool.length === 0 ? (
                    <div className="text-white/40 text-xs py-6 text-center px-4" data-testid="equip-empty">No matching {isGear ? `${pick.slot} gear` : "battle items"} in your bag. Forge some in Adventure.</div>
                  ) : shown.map((it) => {
                    const d = deltasFor(it, stats);
                    return (
                      <button key={it.id} type="button" data-testid={`equip-option-${it.id}`} disabled={equipping}
                        onClick={() => onEquip(it.id)}
                        onMouseEnter={() => setHover(it)} onMouseLeave={() => setHover((h) => (h === it ? null : h))}
                        className={`w-full text-left rounded-lg border p-2 transition-colors hover:bg-white/[0.06] hover:border-cyan-400/50 disabled:opacity-50 ${RRING[it.rarity] || "border-white/12"} bg-white/[0.02]`}>
                        <div className="flex items-center justify-between gap-1">
                          <span className={`text-xs font-medium leading-snug line-clamp-1 ${RTEXT[it.rarity] || "text-white"}`}>{it.name}</span>
                          <span className="font-mono text-[9px] text-white/45 shrink-0"><span style={{ color: TIER_COLOR[it.tier] }}>T{it.tier}</span> · {it.rarity}</span>
                        </div>
                        <div className="flex items-start justify-between gap-2">
                          <StatChips it={it} max={3} size="text-[9px]" />
                          <span className="flex gap-1 shrink-0 mt-1">
                            {STAT_KEYS.map(([k, label]) => d[k] ? <span key={k} className="font-mono text-[9px] text-emerald-400">+{d[k]}{label[0]}</span> : null)}
                          </span>
                        </div>
                      </button>
                    );
                  })}
                </div>
                {pages > 1 && (
                  <div className="flex items-center justify-between px-2 py-1.5 border-t border-white/8">
                    <button data-testid="equip-prev" disabled={cur === 0} onClick={() => setPage(cur - 1)}
                      className="h-6 w-6 rounded flex items-center justify-center text-white/60 hover:text-cyan-300 disabled:opacity-30"><ChevronLeft className="h-4 w-4" /></button>
                    <span className="font-mono text-[9px] text-white/40">Page {cur + 1}/{pages}</span>
                    <button data-testid="equip-next" disabled={cur >= pages - 1} onClick={() => setPage(cur + 1)}
                      className="h-6 w-6 rounded flex items-center justify-center text-white/60 hover:text-cyan-300 disabled:opacity-30"><ChevronRight className="h-4 w-4" /></button>
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
